import { IconBin, IconHeart, IconHeartFilled } from "@tine/designsystem-icons/sharp";
import { Button, InputStepper } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import clamp from "lodash-es/clamp";
import defaultTo from "lodash-es/defaultTo";
import isUndefined from "lodash-es/isUndefined";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "wouter";
import AddToFavoriteModal from "../../account/favorites/AddToFavoriteModal";
import { logger } from "../../common/logger";
import { sendAddCartToFavoriteList, sendAddToCart, sendRemoveFromCart } from "../../common/tracking";
import type { CartItem, Product } from "../../common/types/productTypes";
import { type GAListInformation, LIST_TYPE } from "../../common/types/trackingTypes";
import { formatPrice, formatUnit, inRangeInclusive } from "../../common/utils";
import ProductImage from "../../components/common/product/ProductImage";
import DiscountLabel from "../../components/DiscountLabel";
import DisplayPrice from "../../components/DisplayPrice";
import LeadTimeWarning from "../../components/LeadTimeWarning";
import cartStore from "../../stores/cart/cartStore";
import productStore from "../../stores/product/productStore";
import { isDistributionPack } from "../../stores/product/productUtils";
import promotionStore from "../../stores/promotionStore";
import toastStore from "../../stores/toastStore";

type RecyclingChangeProps = {
   product: Product;
};

const className = cn("border-b-px border-0 border-solid border-b-ink-border-subtle pb-4 last:border-b-0");

const oneBorderBaseCN = cn("border-0 border-solid");
const borderBottomCN = cn(oneBorderBaseCN, "border-b-[1px] border-b-ink-border-subtle");
const borderTopCN = cn(oneBorderBaseCN, "border-t-[1px] border-t-ink-border-subtle");
const desktopClassName = cn(["hidden @[575px]:block"]);
const mobileClassName = cn(["block @[575px]:hidden px-2"]);

const outerRowCN = cn(["flex flex-row justify-stretch"]);

const RecyclingChange = observer(({ product }: RecyclingChangeProps) => {
   if (product.recyclingChargeBaseUnit === 0) {
      return null;
   }

   const conversion = isDistributionPack(product.unit) ? product.conversionFactor : 1;
   const recyclingCharge = product.recyclingChargeBaseUnit * conversion;

   return <small>+ {formatPrice(recyclingCharge)} pant</small>;
});

type MiniCartItemProps = {
   item: CartItem;
   onCartChanged: () => void;
   listInformation: GAListInformation;
};

const MiniCartItem = observer(({ item, onCartChanged, listInformation }: MiniCartItemProps) => {
   const itemPosition = cartStore.items.findIndex((i) => i.sku === item.sku);

   const handleInputStepperChange = action((newQty: number) => {
      if (cartStore.isEditing()) {
         const validRange = cartStore.getValidQtyRange(item.sku);
         item.qty = clamp(newQty, validRange.min, validRange.max);

         if (!inRangeInclusive(newQty, validRange)) {
            toastStore.addError(
               "Begrensninger på ordren",
               `Ordren som du redigerer har begrensninger som gjør at du ikke kan endre antallet på denne varen til ${newQty}.`,
               { context: "orders", text: "change_order_error" }
            );
         }
      } else {
         const oldQty = item.qty;
         item.qty = newQty;
         if (oldQty < newQty) {
            sendAddToCart([{ sku: item.sku, qty: newQty - item.qty }], listInformation);
         } else if (oldQty > newQty) {
            sendRemoveFromCart([{ sku: item.sku, qty: oldQty - newQty, position: itemPosition + 1 }], listInformation);
         }
      }
      cartStore.saveCartToServer();
      cartStore.updateCartDependencies();
      onCartChanged();
      return newQty;
   });

   const product = productStore.resolveSku(item.sku);
   if (isUndefined(product)) {
      logger.warn("cart", `Customer has unavailable product in cart with sku ${item.sku}`);
      cartStore.removeFromCart(item.sku);
      return null;
   }

   const removeFromCart = () => {
      sendRemoveFromCart([{ sku: item.sku, qty: item.qty, position: itemPosition + 1 }], listInformation);
      cartStore.removeFromCart(item.sku);
      onCartChanged();
   };

   return (
      <div className={className}>
         <div className={cn(desktopClassName)}>
            <div className={outerRowCN}>
               <div className="mt-3 grow-0 px-2">
                  <ProductImage product={product} size={48} className="" />
               </div>
               <div className="flex shrink grow flex-col">
                  <div className="flex justify-between">
                     <div className="my-3 py-2 text-base">
                        <Link to={productStore.getUrl(product)}>{product.name}</Link>
                     </div>
                     <Button
                        icon={<IconBin width={24} height={24} />}
                        variant="tertiary"
                        className="self-start"
                        onClick={removeFromCart}
                     />
                  </div>
                  <div className="flex flex-row flex-wrap items-center gap-2 py-1">
                     <DiscountLabel sku={item.sku} />
                     <div className="text-sm">Art. nr: {item.sku}</div>
                  </div>
                  <div className="text-sm font-bold">
                     {formatPrice(product.price)} pr {formatUnit(product.unit)}
                  </div>
                  <RecyclingChange product={product} />
                  <LeadTimeWarning product={product} />
                  <div className="mr-4 mt-4 flex justify-between">
                     <div>
                        <InputStepper min={1} value={item.qty} onChange={handleInputStepperChange} />
                     </div>
                     <DisplayPrice
                        className="min-w-[140px] text-right"
                        price={item.qty * defaultTo(product.priceBeforeDiscount, 0)}
                        newPrice={item.qty * defaultTo(product.price, 0) - promotionStore.getPromotionDiscount(item.sku)}
                     />
                  </div>
               </div>
            </div>
         </div>

         {/* Mobile */}
         <div className={mobileClassName}>
            <div className="mb-4 flex items-center">
               <div className="mr-2 mt-3">
                  <ProductImage product={product} size={48} />
               </div>
               <div className="mt-3">{product.name}</div>
            </div>
            <div className="mb-4 flex flex-wrap items-end justify-between">
               <div className="flex flex-col flex-wrap gap-1">
                  <div className="flex gap-4">
                     <DiscountLabel sku={item.sku} />
                  </div>
                  <div className="text-sm">
                     <span className="font-bold">{formatPrice(product.price)}</span>{" "}
                     <span className="text-xs">per {formatUnit(product.unit)}</span>
                  </div>
               </div>
               <DisplayPrice
                  price={item.qty * defaultTo(product.priceBeforeDiscount, 0)}
                  newPrice={item.qty * defaultTo(product.price, 0) - promotionStore.getPromotionDiscount(item.sku)}
                  className="mt-2"
               />
            </div>
            <div className="flex items-center justify-between">
               <div>
                  <InputStepper value={item.qty} min={1} onChange={handleInputStepperChange} />
               </div>
               <Button variant="tertiary" onClick={removeFromCart}>
                  <IconBin width={24} height={24} />
               </Button>
            </div>
         </div>
      </div>
   );
});

type MiniCartOrderLines = {
   items: CartItem[];
};

const MiniCartOrderLines = ({ items }: MiniCartOrderLines) => {
   const qtySum = items.reduce((p, i) => p + i.qty, 0);
   const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);
   const addToFavoriteText = "Lagre handlekurven som favorittliste";
   const favoriteAddedText = "Lagret som favorittliste";
   const [favoriteListAdded, setFavoriteListAdded] = useState(false);
   const listInformation: GAListInformation = { type: LIST_TYPE.CART, name: "Minicart", length: items.length };

   const heartIcon = favoriteListAdded ? <IconHeartFilled className="text-[--dsp-color-ink-favorites]" /> : <IconHeart />;
   return (
      <>
         <div className={cn("mt-6 px-1 pb-4 text-xxs @[575px]:px-2", borderBottomCN)}>
            {qtySum} produkter | {items.length} varelinjer
         </div>
         <div className="w-full">
            {items.map((ol, _i) => (
               <MiniCartItem
                  key={ol.sku}
                  item={ol}
                  onCartChanged={() => setFavoriteListAdded(false)}
                  listInformation={listInformation}
               />
            ))}
         </div>

         <div className={`flex items-end py-6 ${borderTopCN} [&>button>svg]:hover:text-[--dsp-color-ink-favorites]`}>
            <Button variant="secondary" icon={heartIcon} iconPosition="left" onClick={() => setFavoriteModalOpen(true)}>
               {favoriteListAdded ? favoriteAddedText : addToFavoriteText}
            </Button>
         </div>
         <AddToFavoriteModal
            isOpen={favoriteModalOpen}
            onCloseModal={() => setFavoriteModalOpen(false)}
            favoriteListAdded={() => {
               setFavoriteListAdded(true);
               sendAddCartToFavoriteList("minicart");
            }}
         />
      </>
   );
};

export default observer(MiniCartOrderLines);
